// src/components/project/EditorApp.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Toggle from "react-toggle";
import toast from "react-hot-toast";
import EditorPreview from "./EditorPreview";

export default function EditorApp({
  draftKey,
  initialData = {},
  onSave,
  onSuccess,
  onCancel,
  showPublishToggle = true,
  minSplitPct = 25,
  maxSplitPct = 75,
  mobileBreakpointPx = 768,
}) {
  // Detect dark mode from localStorage (same as DarkModeToggle)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  const [state, setState] = useState(() => ({
    title: "",
    subtitle: "",
    body: "",
    technology: "",
    skill: "",
    publish: false,
    saving: false,
    apiError: null,
    errors: {},
    ...initialData,
  }));

  // --- responsive: compute after mount (no window during init) ---
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(typeof window !== "undefined" && window.innerWidth < mobileBreakpointPx);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [mobileBreakpointPx]);

  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [showGuide, setShowGuide] = useState(false);

  const [split, setSplit] = useState(50);
  const dragging = useRef(false);
  const draftTimeout = useRef(null);

  const inputStyle = useMemo(() => ({
    backgroundColor: "#000000e6",
    color: "#59c6c1",
    borderColor: "#334155",
  }), []);

  // ---- Draft: restore (guarded) ----
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const { picture_link, ...restored } = parsed;
        setState((s) => ({ ...s, ...restored, errors: {}, apiError: null }));
      }
    } catch (e) {
      console.warn("Draft restore failed", e);
    }
  }, [draftKey]);

  // ---- Draft: save (debounced + guarded) ----
  const queueDraftSave = useCallback((nextState) => {
    if (!draftKey) return;
    if (draftTimeout.current) clearTimeout(draftTimeout.current);
    draftTimeout.current = setTimeout(() => {
      try {
        const { errors, apiError, saving, ...clean } = nextState;
        localStorage.setItem(draftKey, JSON.stringify(clean));
      } catch (e) {
        console.warn("Draft save failed", e);
      }
    }, 350);
  }, [draftKey]);

  const setField = useCallback((id, value) => {
    setState((s) => {
      const next = { ...s, [id]: value };
      queueDraftSave(next);
      return next;
    });
  }, [queueDraftSave]);

  const handleChange = (e) => setField(e.target.id, e.target.value);
  const togglePublish = () => setField("publish", !state.publish);

  const clearDraftAndRefresh = () => {
    try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
    if (typeof window !== "undefined") window.location.reload();
  };

  // ---- Validation ----
  const validators = {
    required: (label, v) => (!v?.trim() ? `${label} is required.` : null),
    max: (label, v, n) => (v && v.length > n ? `${label} must be ≤ ${n} characters.` : null),
  };

  const validate = useCallback((s) => {
    const errs = {};
    const add = (k, msg) => msg && (errs[k] = msg);
    add("title", validators.required("Title", s.title) || validators.max("Title", s.title, 150));
    add("subtitle", validators.max("Subtitle", s.subtitle, 150));
    add("technology", validators.max("Technology", s.technology, 150));
    add("skill", validators.max("Skill", s.skill, 150));
    add("body", validators.required("Body", s.body));
    return errs;
  }, []);

  const errors = useMemo(() => validate(state), [state, validate]);
  const hasErrors = Object.keys(errors).length > 0;

  // ---- Save (defensive) ----
  const save = async () => {
    if (typeof onSave !== "function") {
      console.error("EditorApp: onSave is not provided");
      toast.error("Save handler not configured");
      return;
    }
    setState((s) => ({ ...s, saving: true, apiError: null }));
    try {
      const payload = {
        title: state.title,
        subtitle: state.subtitle,
        body: state.body,
        technology: state.technology,
        skill: state.skill,
        publish: state.publish,
      };
      const result = await onSave(payload);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
      toast.success("Project saved");
      setState((s) => ({ ...s, saving: false }));
      const returnedId = result?.id ?? result?.pk;
      if (onSuccess) onSuccess(returnedId);
    } catch (err) {
      console.error("Project Save Failed", err);
      console.error("Project Save Error Response:", err.response);
      console.error("Project Save Error Status:", err.response?.status);
      console.error("Project Save Error Data:", err.response?.data);

      const status = err.response?.status;
      const raw = err?.response?.data;

      // Extract a human-readable message from whatever the server returned
      const extractMessage = (data) => {
        if (!data) return "";
        if (typeof data === "string") return data;
        if (typeof data !== "object") return String(data);
        // DRF: { "detail": "..." }
        if (data.detail) return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
        // DRF: { "error": "..." }
        if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
        // DRF field errors: { "title": ["msg"], "body": ["msg"] }
        const lines = [];
        for (const [k, v] of Object.entries(data)) {
          const msg = Array.isArray(v) ? v.join(" ") : String(v);
          lines.push(`${k}: ${msg}`);
        }
        return lines.join("\n");
      };

      const serverMsg = extractMessage(raw);

      // Field-level errors for inline display
      const serverErrors = (raw && typeof raw === "object" && !Array.isArray(raw))
        ? Object.fromEntries(
            Object.entries(raw)
              .filter(([k]) => !["detail", "error", "non_field_errors"].includes(k))
              .map(([k, v]) => [k, Array.isArray(v) ? v.join(" ") : String(v)])
          )
        : {};

      let errorMessage;
      if (status === 403) {
        errorMessage = serverMsg || "You don't have permission to save this project";
      } else if (status === 500) {
        errorMessage = "Server error occurred while saving project";
      } else if (serverMsg) {
        errorMessage = serverMsg;
      } else {
        errorMessage = `Save failed (${status || "network error"})`;
      }

      toast.error(errorMessage, { duration: 6000, style: { whiteSpace: "pre-wrap" } });
      setState((s) => ({
        ...s,
        apiError: errorMessage,
        errors: { ...s.errors, ...serverErrors },
        saving: false,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasErrors) {
      setState((s) => ({ ...s, errors, apiError: "Please fix the highlighted fields." }));
      return;
    }
    save();
  };

  // ---- Cmd/Ctrl + S ----
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!hasErrors) save();
        else setState((s) => ({ ...s, apiError: "Please fix the highlighted fields." }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasErrors]); // keep short; save uses latest state from closure

  // ---- Drag to resize (desktop only) ----
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => {
      if (!dragging.current) return;
      const container = document.getElementById("pc-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(maxSplitPct, Math.max(minSplitPct, (x / rect.width) * 100));
      setSplit(pct);
    };
    const stop = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
    };
  }, [isMobile, minSplitPct, maxSplitPct]);

  const fieldClass = (key) => (errors[key] ? "pc-field pc-error" : "pc-field");
  const errText = (key) => errors[key] && <div className="pc-error-text">{errors[key]}</div>;
  const gridTemplate = isMobile ? "1fr" : `${split}% 6px ${100 - split}%`;

  const handleCancel = () => (onCancel ? onCancel() : (window.location = "/"));

  const handleJsonImport = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      toast.error("Invalid JSON — expected an object");
      return;
    }
    const knownKeys = ["title", "subtitle", "technology", "skill", "body", "publish"];
    for (const key of knownKeys) {
      if (key in parsed) setField(key, parsed[key]);
    }
    setShowJsonImport(false);
    setJsonText("");
    toast.success("Project imported");
  };

  const aiPrompt = `Write a project write-up for INERTIA7. Return a single JSON object with these fields:

{
  "title": "...",       // ≤ 150 chars. Punchy, concise — renders as H1
  "subtitle": "...",    // ≤ 150 chars. One-liner summary under the title
  "technology": "...",  // ≤ 150 chars. Primary tech (e.g. "PyTorch", "React + D3")
  "skill": "...",       // ≤ 150 chars. Discipline (e.g. "ml engineering", "data viz")
  "body": "..."         // Markdown body — see rules below
}

Body formatting rules:
- Use bullet points instead of tables wherever possible
- Fenced code blocks: use 5 backticks (\`\`\`\`\`) as the fence when the code inside contains triple backticks
- Supported syntax-highlighted languages: python, javascript, bash, json, sql, markdown
- LaTeX math: inline $...$ and block $$...$$
- Do NOT use HTML — use pure Markdown
- Structure with clear headings (## for sections)
- Use bold for key terms on first mention`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Failed to copy")
    );
  };

  return (
    <div id="pc-container" className="pc-container">
      <div className="pc-split" style={{ gridTemplateColumns: gridTemplate }}>
        {/* LEFT: Editor */}
        <form className="pc-pane pc-form" onSubmit={handleSubmit} noValidate>
          <div className="pc-meta-header">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
              <button
                type="button"
                onClick={() => setShowGuide(true)}
                title="Content Guide"
                style={{
                  background: "none", border: "1px solid #334155", borderRadius: "50%",
                  color: "#94a3b8", fontSize: 11, width: 20, height: 20,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0, lineHeight: 1,
                }}
              >
                ?
              </button>
            </div>
            <div className={fieldClass("title")}>
              <label htmlFor="title">Title</label>
              <input
                id="title"
                value={state.title}
                onChange={handleChange}
                placeholder="Punchy, concise title (renders as H1)"
                style={inputStyle}
              />
              {errText("title")}
            </div>

            <div className={fieldClass("subtitle")}>
              <label htmlFor="subtitle">Subtitle</label>
              <input
                id="subtitle"
                value={state.subtitle}
                onChange={handleChange}
                placeholder="Optional one-liner under the title"
                style={inputStyle}
              />
              {errText("subtitle")}
            </div>

            <div className="pc-meta-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className={fieldClass("technology")}>
                <label htmlFor="technology">Technology</label>
                <input
                  id="technology"
                  value={state.technology}
                  onChange={handleChange}
                  placeholder="e.g. PyTorch"
                  style={inputStyle}
                />
                {errText("technology")}
              </div>

              <div className={fieldClass("skill")}>
                <label htmlFor="skill">Skill</label>
                <input
                  id="skill"
                  value={state.skill}
                  onChange={handleChange}
                  placeholder="e.g. ml engineering"
                  style={inputStyle}
                />
                {errText("skill")}
              </div>
            </div>

            {state.apiError && (
              <div className="pc-alert" role="alert" style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                {state.apiError}
              </div>
            )}
          </div>

          <div className="pc-body-scroll">
            <div className={fieldClass("body")} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <label htmlFor="body" style={{ marginBottom: 4 }}>Body</label>
              <small className="pc-help" style={{ marginBottom: 6 }}>
                Markdown supported. Code blocks are highlighted.
              </small>
              <textarea
                id="body"
                value={state.body}
                onChange={handleChange}
                style={{ ...inputStyle, flex: "1 1 auto", minHeight: 220, resize: "vertical" }}
                placeholder={`# Introduction
Brief context, approach, and results.

\`\`\`python
# code blocks get syntax highlighting
import numpy as np
\`\`\`
`}
              />
              {errText("body")}
            </div>
          </div>

          <div className="pc-toolbar">
            <div className="pc-toolbar-left">
              {showPublishToggle && (
                <label className="pc-toggle">
                  <Toggle checked={state.publish} onChange={togglePublish} />
                  <span className="pc-status">{state.publish ? "Save as public" : "Save as private"}</span>
                </label>
              )}
            </div>

            <div className="pc-toolbar-right">
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowJsonImport(true)} title="Import project fields from JSON">
                Import JSON
              </button>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={clearDraftAndRefresh} title="Clear local draft and refresh">
                Clear
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                onClick={handleCancel}
                style={{ background: "#fff", color: "#334155", borderColor: "#e2e8f0" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="submit"
                disabled={state.saving}
                style={{ background: "#59c6c1", borderColor: "#59c6c1", color: "#0f172a" }}
              >
                {state.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>

        {/* Gutter / Preview only on desktop */}
        {!isMobile && <div className="pc-gutter" onMouseDown={() => (dragging.current = true)} title="Drag to resize" />}
        {!isMobile && (
          <EditorPreview
            title={state.title}
            subtitle={state.subtitle}
            technology={state.technology}
            skill={state.skill}
            body={state.body}
            publish={state.publish}
            theme={isDarkMode ? "dark" : "light"}
          />
        )}
      </div>

      {showJsonImport && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowJsonImport(false)}
        >
          <div
            style={{
              background: "#1e293b", borderRadius: 8, padding: 24,
              width: "90%", maxWidth: 520, color: "#e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: "0 0 12px", color: "#59c6c1" }}>Import JSON</h4>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#94a3b8" }}>
              Paste a JSON object with any of: title, subtitle, technology, skill, body, publish
            </p>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={10}
              style={{
                width: "100%", fontFamily: "monospace", fontSize: 13,
                background: "#000000e6", color: "#59c6c1", border: "1px solid #334155",
                borderRadius: 4, padding: 10, resize: "vertical",
              }}
              placeholder={'{\n  "title": "...",\n  "subtitle": "...",\n  "technology": "...",\n  "skill": "...",\n  "body": "..."\n}'}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => { setShowJsonImport(false); setJsonText(""); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                onClick={handleJsonImport}
                style={{ background: "#59c6c1", borderColor: "#59c6c1", color: "#0f172a" }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {showGuide && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowGuide(false)}
        >
          <div
            style={{
              background: "#1e293b", borderRadius: 8, padding: 24,
              width: "90%", maxWidth: 600, maxHeight: "85vh", overflowY: "auto",
              color: "#e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: "0 0 16px", color: "#59c6c1" }}>Content Guide</h4>

            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>Field Limits</h5>
              <ul style={{ margin: "0 0 16px", paddingLeft: 20 }}>
                <li><strong>Title</strong> — required, max 150 chars. Renders as H1.</li>
                <li><strong>Subtitle</strong> — optional, max 150 chars. One-liner under the title.</li>
                <li><strong>Technology</strong> — optional, max 150 chars (e.g. "PyTorch", "React + D3").</li>
                <li><strong>Skill</strong> — optional, max 150 chars (e.g. "ml engineering").</li>
                <li><strong>Body</strong> — required. Markdown.</li>
              </ul>

              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>Formatting Best Practices</h5>
              <ul style={{ margin: "0 0 16px", paddingLeft: 20 }}>
                <li>Use <strong>bullet points instead of tables</strong> — tables scroll horizontally and can be hard to read.</li>
                <li>Code blocks: use <strong>5 backticks</strong> (<code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>`````</code>) as the fence when code inside contains triple backticks.</li>
                <li>Supported highlighted languages: <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>python</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>javascript</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>bash</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>json</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>sql</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>markdown</code>.</li>
                <li>LaTeX math: inline <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>$...$</code> and block <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>$$...$$</code>.</li>
                <li>Structure with <strong>## headings</strong> for sections.</li>
                <li>Bold key terms on first mention.</li>
                <li>Stick to pure Markdown — avoid raw HTML.</li>
              </ul>

              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>AI Prompt</h5>
              <p style={{ color: "#94a3b8", margin: "0 0 8px" }}>
                Copy this prompt and paste it into Claude or ChatGPT. It will return a JSON object you can import directly with "Import JSON".
              </p>
              <pre
                onClick={() => copyToClipboard(aiPrompt)}
                style={{
                  background: "#000000e6", color: "#59c6c1", border: "1px solid #334155",
                  borderRadius: 4, padding: 12, fontSize: 12, whiteSpace: "pre-wrap",
                  wordBreak: "break-word", maxHeight: 240, overflowY: "auto",
                  cursor: "pointer", transition: "border-color 150ms ease",
                }}
                title="Click to copy"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#59c6c1")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#334155")}
              >{aiPrompt}</pre>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => setShowGuide(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
